import { useRef } from 'react'

import { Box, Button, Card, Form, Section } from '@factorialco/deprecated-design-system'
import {
  RichTextEditor,
  RichTextEditorHandle,
} from '@factorialco/factorial-one-react/dist/experimental'

import { observer } from 'mobx-react'

import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AtsMessageVariablesSelector } from 'modules/ats/components/AtsMessageVariablesSelector'
import type GDPRDataRetention from 'modules/ats/store/models/GDPRDataRetention'

interface EmailConsentRenewalForm {
  consentRenewalEmailEnabled: boolean
  consentRenewalEmailSubject: string
  consentRenewalEmailBody: string
}

interface Props {
  currentRetention: GDPRDataRetention
}

const EmailConsentRenewal = ({ currentRetention }: Props) => {
  const { canUseRichTextEditor, loading: richTextEditorLoading, editorConfig } = useRichTextEditor()
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)

  const subjectPlaceholder = i18n.t(
    'ats.settings.candidate_data.data_retention_request_email.text_editor.title_placeholder'
  )

  const bodyPlaceholder = i18n.t(
    'ats.settings.candidate_data.data_retention_request_email.text_editor.content_placeholder'
  )

  const form = useForm<EmailConsentRenewalForm>({
    defaultValues: {
      consentRenewalEmailEnabled: currentRetention.attributes.consent_renewal_email_enabled,
      consentRenewalEmailSubject:
        currentRetention.attributes.consent_renewal_email_subject || subjectPlaceholder,
      consentRenewalEmailBody:
        currentRetention.attributes.consent_renewal_email_body || bodyPlaceholder,
    },
    validate: (formValues) => {
      const errors: FormErrors<EmailConsentRenewalForm> = {}

      if (formValues.consentRenewalEmailEnabled) {
        if (!formValues?.consentRenewalEmailSubject.trim()) {
          errors.consentRenewalEmailSubject = i18n.t('common.empty')
        }

        if (!formValues.consentRenewalEmailBody?.trim()) {
          errors.consentRenewalEmailBody = i18n.t('common.empty')
        }
      }

      return errors
    },
    onSubmit: async (formValues) => {
      const updatedData = await currentRetention?.save({
        consent_renewal_email_enabled: formValues.consentRenewalEmailEnabled,
        consent_renewal_email_subject: formValues.consentRenewalEmailSubject,
        consent_renewal_email_body: formValues.consentRenewalEmailBody,
      })

      if (updatedData) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return updatedData
    },
  })

  const onSubmit = () => form.handleSubmit()

  const onToggle = (value: boolean) => {
    form.setValues({
      ...form.values,
      consentRenewalEmailEnabled: value,
    })

    onSubmit()
  }

  const onSelectVariable = (variable: string) => {
    const content = `${form.values.consentRenewalEmailBody} ${variable}`
    form.setValues({
      ...form.values,
      consentRenewalEmailBody: content,
    })
    if (canUseRichTextEditor) {
      if (richTextEditorRef && 'current' in richTextEditorRef) {
        richTextEditorRef.current?.setContent(content)
      }
    }
  }

  const toggleEnabled = form.values.consentRenewalEmailEnabled
  const submitDisabled = !currentRetention?.attributes || form.isSubmitting || !form.isValid

  return (
    <Section.TwoThirds
      description={i18n.t('ats.settings.candidate_data.data_retention_request_email.description')}
      illustration="automations-default"
      title={i18n.t('ats.settings.candidate_data.data_retention_request_email.title')}
    >
      <Card>
        <Card.Title size="small">
          {i18n.t('ats.settings.candidate_data.data_retention_request_email.toggle.title')}
        </Card.Title>
        <Card.Description>
          {i18n.t('ats.settings.candidate_data.data_retention_request_email.toggle.description')}
        </Card.Description>
        <Card.ExpandableContent expanded={toggleEnabled} onExpandChange={onToggle}>
          <Form onSubmit={onSubmit} padding={null}>
            <Box flexDirection="row" alignItems="flexEnd" justifyContent="spaceBetween" gap="s8">
              <Box flexGrow>
                <TextField
                  label={i18n.t(
                    'ats.settings.candidate_data.data_retention_request_email.text_editor.title'
                  )}
                  value={form.values.consentRenewalEmailSubject}
                  onChange={(value) => form.setFieldValue('consentRenewalEmailSubject', value)}
                  error={form.errors.consentRenewalEmailSubject}
                />
              </Box>
              <AtsMessageVariablesSelector onSelect={onSelectVariable} />
            </Box>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <RichTextEditor
                ref={richTextEditorRef}
                {...editorConfig}
                title={i18n.t(
                  'ats.settings.candidate_data.data_retention_request_email.text_editor.title'
                )}
                onChange={(data) => form.setFieldValue('consentRenewalEmailBody', data?.value)}
                placeholder={i18n.t(
                  'ats.settings.candidate_data.data_retention_request_email.text_editor.content_placeholder'
                )}
                initialEditorState={{
                  content: form.values.consentRenewalEmailBody ?? '',
                }}
                maxCharacters={10000}
              />
            ) : (
              <TextEditor
                placeholder=""
                initialEditorState={{
                  data: form.values.consentRenewalEmailBody,
                }}
                height="s200"
                onChange={(value) => form.setFieldValue('consentRenewalEmailBody', value)}
              />
            )}
            <Form.Buttons>
              <Button submit disabled={submitDisabled}>
                {i18n.t('common.submit')}
              </Button>
            </Form.Buttons>
          </Form>
        </Card.ExpandableContent>
      </Card>
    </Section.TwoThirds>
  )
}

export default observer(EmailConsentRenewal)
